import { useState, useCallback, useRef } from "react"
import { BrotherPrinterService, GroupData, GuestData } from "../../external/brotherPrinter"

export enum PrintStatus {
    IDLE = "IDLE",
    PRINTING = "PRINTING",
    SUCCESS = "SUCCESS",
    ERROR = "ERROR",
    NOT_INSTALLED = "NOT_INSTALLED",
}

interface UseLabelPrinterProps {
    folderName?: string
    onPrintComplete?: (success: boolean) => void
}

export function useLabelPrinter({ folderName, onPrintComplete }: UseLabelPrinterProps = {}) {
    const [printStatus, setPrintStatus] = useState<PrintStatus>(PrintStatus.IDLE)
    const [errorMessage, setErrorMessage] = useState<string>("")
    const [currentLabelIndex, setCurrentLabelIndex] = useState<number>(0)
    const [totalLabels, setTotalLabels] = useState<number>(0)
    const cancelPrintingRef = useRef<boolean>(false)

    const printGuestLabel = useCallback(
        async (guestData: GuestData) => {
            if (!BrotherPrinterService.checkExtensionInstalled()) {
                setPrintStatus(PrintStatus.NOT_INSTALLED)
                return
            }
            if (!guestData.visitorName || !guestData.hostName) {
                setPrintStatus(PrintStatus.ERROR)
                return
            }

            setPrintStatus(PrintStatus.PRINTING)
            setErrorMessage("")
            setCurrentLabelIndex(1)
            setTotalLabels(1)

            try {
                const success = await BrotherPrinterService.printGuestLabel(guestData, folderName)
                setPrintStatus(success ? PrintStatus.SUCCESS : PrintStatus.ERROR)
                if (!success) {
                    setErrorMessage("Failed to print guest label")
                }
                onPrintComplete?.(success)
            } catch (error: any) {
                setPrintStatus(PrintStatus.ERROR)
                setErrorMessage(error?.message || "Unknown error printing guest label")
                onPrintComplete?.(false)
            } finally {
                setCurrentLabelIndex(0)
            }
        },
        [folderName, onPrintComplete],
    )

    const printMultipleGuestLabels = useCallback(
        async (guestDataList: GuestData[]) => {
            if (!BrotherPrinterService.checkExtensionInstalled()) {
                setPrintStatus(PrintStatus.NOT_INSTALLED)
                return
            }
            if (!guestDataList.length) {
                setErrorMessage("No labels to print")
                setPrintStatus(PrintStatus.ERROR)
                return
            }

            setPrintStatus(PrintStatus.PRINTING)
            setErrorMessage("")
            setTotalLabels(guestDataList.length)
            cancelPrintingRef.current = false

            let allSuccessful = true

            for (let i = 0; i < guestDataList.length; i++) {
                // Check if printing was cancelled
                if (cancelPrintingRef.current) {
                    setErrorMessage("Printing cancelled by user")
                    setPrintStatus(PrintStatus.ERROR)
                    onPrintComplete?.(false)
                    break
                }

                const guestData = guestDataList[i]
                setCurrentLabelIndex(i + 1)

                if (!guestData.visitorName || !guestData.hostName) {
                    continue // Skip invalid data
                }

                try {
                    const success = await BrotherPrinterService.printGuestLabel(guestData, folderName)
                    if (!success) {
                        allSuccessful = false
                        setErrorMessage(`Failed to print label for ${guestData.visitorName}`)
                    }
                } catch (error: any) {
                    allSuccessful = false
                    setErrorMessage(error?.message || `Error printing label for ${guestData.visitorName}`)
                }
            }

            if (!cancelPrintingRef.current) {
                setPrintStatus(allSuccessful ? PrintStatus.SUCCESS : PrintStatus.ERROR)
                onPrintComplete?.(allSuccessful)
            }

            setCurrentLabelIndex(0)
        },
        [folderName, onPrintComplete],
    )

    const printGroupLabels = useCallback(
        async (groupData: GroupData) => {
            if (!groupData.groupName || !groupData.hostName || groupData.groupSize < 1) {
                setErrorMessage("Please fill in all required fields and ensure group size is at least 1")
                setPrintStatus(PrintStatus.ERROR)
                return
            }

            setPrintStatus(PrintStatus.PRINTING)
            setErrorMessage("")
            setTotalLabels(groupData.groupSize)

            try {
                const success = await BrotherPrinterService.printGroupLabels(groupData, folderName)
                setPrintStatus(success ? PrintStatus.SUCCESS : PrintStatus.ERROR)
                if (!success) {
                    setErrorMessage("Failed to print group labels")
                }
                onPrintComplete?.(success)
            } catch (error: any) {
                setPrintStatus(PrintStatus.ERROR)
                setErrorMessage(error?.message || "Unknown error printing group labels")
                onPrintComplete?.(false)
            } finally {
                setCurrentLabelIndex(0)
            }
        },
        [folderName, onPrintComplete],
    )

    const cancelPrinting = useCallback(() => {
        cancelPrintingRef.current = true
    }, [])

    const clearState = useCallback(() => {
        setPrintStatus(PrintStatus.IDLE)
        setErrorMessage("")
        setCurrentLabelIndex(0)
        setTotalLabels(0)
        cancelPrintingRef.current = false
    }, [])

    const isLoading = printStatus === PrintStatus.PRINTING
    const hasError = printStatus === PrintStatus.ERROR
    const isInstalledError = printStatus === PrintStatus.NOT_INSTALLED
    const hasSuccess = printStatus === PrintStatus.SUCCESS

    return {
        //actions
        printGuestLabel,
        printMultipleGuestLabels,
        printGroupLabels,
        clearState,
        cancelPrinting,

        //state
        isInstalledError: isInstalledError,
        printStatus,
        printErrorMessage: errorMessage,
        isLoading,
        hasError,
        hasSuccess,
        currentLabelIndex,
        totalLabels,
    }
}
